import type { SubjectSuggestion } from "@/main/common/SubjectSuggestionService";

/**
 * What the author has chosen in the subject combobox.
 *
 * <p>Two modes, and the distinction is load-bearing all the way to the server. `existing` carries a
 * `publicId` and attaches by id — the write path never name-matches (technical design §4.2).
 * `new` carries only a name, and the subject is created server-side at draft-save/publish, which is
 * what keeps subject creation behind the publish path's rate limits and means an abandoned draft
 * never litters the subject table (§4.5).
 *
 * <p>`name` is present in both modes because the trigger and the review header have to render
 * something before any round trip: for `existing` it is the suggestion's display name, for `new` it
 * is exactly what the author typed.
 */
export type SubjectSelection =
    | { mode: "existing"; publicId: string; name: string }
    | { mode: "new"; name: string };

/** The four fields the publish/draft request carries for the REVIEW facet. Mirrors `RequestWithReviewFacet` on the server. */
export type ReviewFacetRequestFields = {
    reviewFacetSelected: boolean;
    reviewSubjectPublicId?: string;
    reviewSubjectName?: string;
    reviewRatingHalfStars?: number;
};

/**
 * The REVIEW facet's live state inside the composer and the post-publish edit dialog.
 *
 * <p>Rating is `null` rather than `0` when unset: there is no zero rating — the database CHECK is
 * `1..10` — so a zero would be a value the server cannot store masquerading as "none".
 */
export type ReviewFacetState = {
    subject: SubjectSelection | null;
    ratingHalfStars: number | null;
};

export const EMPTY_REVIEW_FACET_STATE: ReviewFacetState = { subject: null, ratingHalfStars: null };

export const REVIEW_RATING_HALF_STARS_MIN = 1;
export const REVIEW_RATING_HALF_STARS_MAX = 10;

/**
 * Maps the live state onto the request fields.
 *
 * <p>`reviewFacetSelected` is ALWAYS sent, never omitted when false — `false` is the instruction
 * that REMOVES the facet from a post being re-published, and omitting it would make removal
 * unrepresentable. Same rule the QUESTION chip follows (`CreateDiscussion.tsx`).
 */
export function toReviewFacetRequestFields(
    selected: boolean,
    state: ReviewFacetState,
): ReviewFacetRequestFields {
    if (!selected) {
        return { reviewFacetSelected: false };
    }
    return {
        reviewFacetSelected: true,
        reviewSubjectPublicId: state.subject?.mode === "existing" ? state.subject.publicId : undefined,
        reviewSubjectName: state.subject?.mode === "new" ? state.subject.name : undefined,
        reviewRatingHalfStars: state.ratingHalfStars ?? undefined,
    };
}

/** Rehydrates the combobox from a reopened draft, which persists only the id — never the typed name. */
export function subjectSelectionFromDraft(
    publicId: string | null | undefined,
    name: string | null | undefined,
): SubjectSelection | null {
    const trimmedId = publicId?.trim();
    if (trimmedId) {
        return { mode: "existing", publicId: trimmedId, name: name?.trim() || trimmedId };
    }
    const trimmedName = name?.trim();
    return trimmedName ? { mode: "new", name: trimmedName } : null;
}

export function subjectSelectionFromSuggestion(suggestion: SubjectSuggestion): SubjectSelection {
    return { mode: "existing", publicId: suggestion.publicId, name: suggestion.name };
}

/**
 * Adopt the public id the server minted for a subject the author typed inline.
 *
 * <p>Without this the composer stays in `new` mode forever and re-sends the NAME on every write, so
 * the server's "a name arrived and no id did" branch is reached by every autosave and again at
 * publish — which is exactly how one publish came to mint two subjects (as-built divergence #29).
 * The server now reuses the draft's own subject when the tightened name is unchanged, but that is a
 * guard compensating for a client that never adopted the id; this closes it at the source, and with
 * it the double-submit race the server-side guard narrows but cannot eliminate.
 *
 * <p><b>Guarded on the name we actually SENT, not on the current one.</b> The response is async: an
 * author who kept typing after the save left has a newer name in the box, and promoting that to an
 * id minted for the OLD name would silently attach the review to the wrong subject — a defect with
 * no visible symptom, since the combobox would go on displaying the newer text. When the names have
 * diverged, the next save re-sends the new name and mints (or reuses) against that instead.
 *
 * <p>Returns the SAME state object when nothing is adopted, so a caller can compare by identity and
 * skip a needless re-render (AGENTS.md rule 31).
 */
export function adoptCreatedSubject(
    state: ReviewFacetState,
    sentName: string | undefined,
    persistedPublicId: string | null | undefined,
): ReviewFacetState {
    const publicId = persistedPublicId?.trim();
    const requestedName = sentName?.trim();
    if (!publicId || !requestedName || state.subject?.mode !== "new") {
        return state;
    }
    if (state.subject.name.trim() !== requestedName) {
        return state;
    }
    return { ...state, subject: { mode: "existing", publicId, name: state.subject.name } };
}
